<template lang="pug">
.flip-cards.cards.flex.flex-wrap.justify-center.items-stretch.w-full.mb-14(
  :class="`cards--${screen.cards.length}`"
  v-if="isValidData"
)
  .flip-card.card(
    :class="mergeStyles(`slide-up card--cols-${card.cols || screen.cols || 6}`, card.style)"
    v-for="(card, i) in screen.cards"
  )
    audio-marker(:name="`${screen.id}-flipcard-${i}`")
    uc-preload-sfx(name="flip")
    button.flip.z-20.fill.w-full.h-full(
      :aria-label="normalizeMarkdown(card.heading || card.subheading || card['img-alt'] || '')"
      :data-index="i"
      :id="`flipcard_${screen.id}_${i}`"
      @click="clickHandler"
      data-sfx="flip"
      uc-word-skip=""
      v-if="!isEditModeActive"
    )

    //- special edit-mode flip button so we can click to edit on the card
    button.flip-edit.rounded-lg.px-2.py-1.bg-indigo-700.hover_bg-indigo-500.absolute.top-1.right-1.border-white.text-white.z-50(
      :data-index="i"
      :id="`flipcard_${screen.id}_${i}`"
      @click="clickHandler"
      data-sfx="flip"
      uc-word-skip=""
      v-if="isEditModeActive"
    )
      uc-icon.text-lg.mr-1(icon="mdi:refresh")
      uc-text.text-sm(text="Flip")

    .flip-content.w-full.surface.text-center.block(
      :class="[{ 'flip-content--flipped': flipped[i] }, mergeStyles(screen.styles?.card, screen.styles?.front), { 'corners overflow-hidden': !isEditModeActive }]"
    )
      .flip-front.prose-stack.overflow-hidden(
        :class="[mergeStyles('bg-secondary-500', screen.styles?.card, screen.styles?.front), mergeStyles('bg-cover', card.styles?.['bg-front'])]"
        uc-word-skip=""
        :style="[card['bg-front'] ? { backgroundImage: setBgImgURLs(card['bg-front']) } : {}]"
      )

        uc-asset.mx-auto(
          :size="validateImgSize(card['img-size'])"
          :src="card['img-top']"
          :class="card?.styles?.img"
          v-if="!card.img && card['img-top']"
        )
        uc-editable.flip-front-heading.font-bold(
          :class="card.styles?.heading || card.styles?.subheading"
          :edit-path="card.heading ? `${editPath}.cards.${i}.heading` : `${editPath}.cards.${i}.subheading`"
          :k="card.heading ? `${card.id}.heading` : `${card.id}.subheading`"
          type="h4"
          :text="card.heading || card.subheading"
          v-if="card.heading || card.subheading"
        )
        uc-asset.mx-auto(
          :size="validateImgSize(card['img-size'])"
          :src="card.img"
          :class="card?.styles?.img"
          v-if="card.img"
        )

      .flip-back.invisible.prose-stack(
        :class="[mergeStyles('bg-secondary-500', screen.styles?.card, screen.styles?.back), mergeStyles('bg-cover', card.styles?.['bg-back'])]"
        tabindex="0"
        :style="[card['bg-back'] ? { backgroundImage: setBgImgURLs(card['bg-back']) } : {}]"
      )
        uc-editable.flip-back-heading.font-bold.dark_text-white(
          :class="card.styles?.heading || card.styles?.subheading"
          :edit-path="card.heading ? `${editPath}.cards.${i}.heading` : `${editPath}.cards.${i}.subheading`"
          :k="card.heading ? `${card.id}.heading` : `${card.id}.subheading`"
          :text="card.heading || card.subheading"
          type="h4"
          v-if="(card.heading || card.subheading) && (wordDoc || !card['no-title'])"
        )

        uc-editable.flip-back-text.prose(
          :class="mergeStyles(card['no-title'] && `flex flex-col flex-grow justify-center`, card.styles?.text)"
          :edit-path="`${editPath}.cards.${i}.text`"
          :k="card.id + '.text'"
          :text="card.text"
          v-if="card.text"
        )
</template>

<script setup lang="ts">
import mergeStyles from '../lib/mergeStyles';
import {
  nextTick,
  onMounted,
  inject,
  reactive,
  computed,
  watch,
  ref,
  toRef,
  withDefaults,
  type Ref
} from 'vue';
import { setCompleted, set } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import { play, stop, queue } from '../lib/audio';
import normalizeMarkdown from '../lib/normalizeMarkdown';
import isQuizPage from '../lib/isQuizPage';
import bugAlert from '../lib/bugAlert';
import expandUploadPath from '../lib/expandUploadPath';
import useScreenAudio from '../lib/useScreenAudio';
import flipCardsSchema from '../lib/screen-schemas/screen-flip-cards.schema';

const setBgImgURLs = (path: string) => {
  const paths = path.trim().split(' ').map(expandUploadPath);
  return paths.length === 1
    ? `url(${paths[0]})`
    : paths.map(p => `url(${p})`).join(', ');
};

const wordDoc = inject('wordDoc');
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;

type Cols = 2 | 3 | 4 | 6 | 12;
type Card = UcScreen & { cols: Cols };

const props = withDefaults(
  defineProps<{
    screen: UcScreen & {
      cards: Card[];
      cols?: Cols;
      'no-title'?: boolean;
      styles?: { front?: string; back?: string };
    };
    editPath: string;
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);

const suspend = useSuspend();
const flipped = reactive({});
const sectionId = inject('sectionId') as string;
const pageId = inject('pageId') as object;
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));

const { playSlot, queueSlot } = useScreenAudio(
  toRef(props, 'screen'),
  flipCardsSchema
);

// Tracks the most recently flipped-to-back card index for re-entry audio queuing
const lastFlipped = ref<number | null>(null);

const clickHandler = async (e: MouseEvent) => {
  const el = e.currentTarget as HTMLElement;
  const cardId = el.id;
  const index = el.dataset.index as string;
  const i = Number(index);
  setCompleted(cardId);

  flipped[index] = !flipped[index];

  if (flipped[index]) {
    lastFlipped.value = i;
    stop({ fadeOut: 0.4 });
    const card = props.screen.cards[i];
    const propAudio = card?.audio || card?.speech;
    if (propAudio) {
      play(propAudio, { delay: 0.6 });
    } else {
      playSlot(`flipcard-${i}`);
    }
  } else {
    stop({ fadeOut: 0.4 });
  }

  await nextTick();

  if (allToggleCompleted()) {
    setCompleted(sectionId);
  }
};

const allToggleCompleted = () => {
  const id = props.screen.id;
  let allComplete = true;
  let i = props.screen.cards.length;

  while (i-- && allComplete) {
    const flipcardId = `flipcard_${id}_${i}`;

    if (!suspend.completed[flipcardId]) {
      allComplete = false;
    }
  }

  return allComplete;
};

const reset = () => {
  let i = props.screen.cards.length;

  while (i--) {
    const flipcardId = `flipcard_${props.screen.id}_${i}`;
    set(`completed.${flipcardId}`, false);
    flipped[i] = false;
  }
};

const canReset = e => {
  let canReset = false;
  const quizzes = e.detail?.quizzes ? e.detail.quizzes : [];
  quizzes.forEach(q => {
    getQuizUnits(q)?.forEach(q => {
      if (!quizzes.includes(q)) {
        canReset = true;
      }
    });
  });
  return canReset;
};

// cols: fit-5 does not work well with flip-cards and large images UFD-416
const validateImgSize = (size: number) => {
  if (!size) return 6;

  if (props.screen.cols && props.screen.cols === 'fit-5' && size > 6) {
    bugAlert(
      `Screen #${props.screen.id}\n\nThe image size for one of the flip-cards is too large for 'cols: fit-5'. It has been automatically set to 6.`
    );
    return 6;
  }

  return size;
};

const isValidData = computed(() => {
  let isValidYaml = true;
  let invalidCards: number[] = [];
  props.screen.cards.forEach((card, i) => {
    if (!card['img-alt'] && !card.heading && !card.subheading) {
      isValidYaml = false;
      invalidCards.push(i + 1);
    }
  });

  // TODO - add robust error messaging that does not interupt workflow
  // if (!isValidYaml) {
  //   bugAlert(
  //     `Screen #${props.screen.id}\n\nCard/s ${invalidCards.join(',')} are missing a heading, subheading or img-alt. If the flip-card back has no heading or subheading please add a 'img-alt' field for use with screen readers.`
  //   );
  // }

  return true;
});

// Schema audio: play intro on section activation; if a card has been flipped, queue it.
const playSectionAudio = async () => {
  await playSlot('intro');
  if (lastFlipped.value !== null) {
    const i = lastFlipped.value;
    const card = props.screen.cards[i];
    const propAudio = card?.audio || card?.speech;
    if (propAudio) {
      queue(propAudio, { owner: props.screen.id });
    } else if (i && flipped[i]) {
      queueSlot(`flipcard-${i}`);
    }
  }
};

watch(sectionActive, active => {
  if (props.nested || !active) return;
  void playSectionAudio();
});

// The first section on a page never receives a false→true sectionActive
// transition, so play its intro here (mirrors uc-screen.vue).
onMounted(async () => {
  await nextTick();
  if (!props.nested && sectionActive.value) void playSectionAudio();
});
</script>

<style scoped>
.flip-card {
  perspective: 1000px;
  display: block;
  /* width: 100%; */
  transition: filter 200ms;

  &:hover {
    filter: brightness(1.05);
  }
}

.flip-card button.flip {
  cursor: pointer !important;
}

.flip-content {
  transition: all 600ms;
  transform-style: preserve-3d;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  width: 100%;
  height: 100%;
}

.flip-front,
.flip-back {
  transform: rotateX(0deg);
  grid-row-start: 1;
  grid-column-start: 1;
  transition-delay: 180ms;
  height: 100%;
  width: 100%;
  max-width: 100%;
}

.flip-front {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 3rem;
  padding-bottom: 3rem;
  align-self: center;
  align-items: center;
  min-height: 100%;
  justify-content: center;
}

.flip-back {
  @apply flex flex-col gap-4 p-6;
  text-align: center;
  transform: translateY(-100%);
  transform: rotateY(180deg);
}

.flip-content.flip-content--flipped {
  transform: rotateY(180deg);

  @media not all and (min-resolution: 0.001dpcm) {
    @supports (-webkit-appearance: none) {
      /* Safari Only CSS here */
      filter: brightness(1);
    }
  }

  .flip-back {
    visibility: visible;
  }

  .flip-front {
    visibility: hidden;
  }

  &:where(.dark *) {
    @apply bg-gray-600;
  }
}

.flip-front .icon {
  @apply text-white;
}

button:focus-visible ~ .flip-content {
  @apply ring-8;
}
</style>
